import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { supabase } from "@/lib/supabase";
import { fileUtils } from "@/lib/utils/fileUtils";
import { formatCount } from "@/lib/utils/formatCount";
import { ChatCompletionService } from "@/services/openai/chatCompletionService";
import useAssistantStore from "@/store/assistantStore";
import useAuthStore from "@/store/authStore";
import { useThreadStore } from "@/store/index";
import { FileJson, FileText, Users } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Create service instance
const chatCompletionService = new ChatCompletionService();

import WorkspaceChats from "@/components/workspace/WorkspaceChats";
import WorkspaceHeader from "@/components/workspace/WorkspaceHeader";
import AssistantManagementDialog from "@/components/workspace/dialogs/AssistantManagementDialog";
import EditProjectDialog from "@/components/workspace/dialogs/EditProjectDialog";
import ManageTeamDialog from "@/components/workspace/dialogs/ManageTeamDialog";
import { ProjectRenameDialog } from "@/features/sidebar/projects/dialogs/ProjectRenameDialog";
import { ProjectDeleteDialog } from "@/features/sidebar/projects/dialogs/ProjectDeleteDialog";
import { useToast } from "@/hooks/useToast";

const SkeletonStyles = () => (
  <style>{`
    @keyframes slowPulse {
      0%, 100% {
        opacity: 0.6;
      }
      50% {
        opacity: 0.4;
      }
    }
    .skeleton-pulse {
      animation: slowPulse 3s ease-in-out infinite;
    }
  `}</style>
);

function WorkspacePageSkeleton() {
  return (
    <div className="flex flex-col min-h-screen bg-transparent">
      <div className="flex-1 container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        <div className="flex flex-col space-y-3 mb-8">
          <div className="flex justify-between items-center">
            <div className="h-8 w-36 sm:w-48 bg-gray-200 dark:bg-zinc-800 rounded-md skeleton-pulse"></div>
            <div className="h-10 w-24 sm:w-36 bg-gray-200 dark:bg-zinc-800 rounded-md skeleton-pulse"></div>
          </div>
          <div className="h-6 w-full sm:w-96 max-w-full bg-gray-200 dark:bg-zinc-800 rounded-md skeleton-pulse"></div>
        </div>

        <div className="space-y-4 sm:space-y-6 lg:space-y-10">
          <div className="w-full mb-4 sm:mb-6 flex justify-center">
            <div className="bg-background/30 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden w-full sm:w-3/4 max-w-4xl h-16 skeleton-pulse"></div>
          </div>

          <div className="w-full mb-4 sm:mb-6 flex justify-center">
            <div className="w-full sm:w-3/4 max-w-4xl grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="p-3 sm:p-4 rounded-2xl bg-zinc-900/50 border border-zinc-800 h-auto sm:h-24 skeleton-pulse">
                <div className="flex items-start">
                  <div className="h-5 w-5 bg-zinc-800 rounded-full mr-3 mt-1"></div>
                  <div>
                    <div className="h-5 w-24 bg-zinc-800 rounded mb-2"></div>
                    <div className="h-3 w-full sm:w-48 bg-zinc-800 rounded-md"></div>
                  </div>
                </div>
              </div>
              <div className="p-3 sm:p-4 rounded-2xl bg-zinc-900/50 border border-zinc-800 h-auto sm:h-24 skeleton-pulse">
                <div className="h-5 w-24 bg-zinc-800 rounded mb-2"></div>
                <div className="h-3 w-full sm:w-48 bg-zinc-800 rounded-md"></div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <div className="w-full sm:w-3/4 max-w-4xl">
              <div className="flex justify-start mb-4">
                <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-zinc-800 skeleton-pulse"></div>
              </div>
              <div className="space-y-2">
                {[1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className="w-full h-8 flex-shrink-0 rounded-lg p-2 border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 flex items-center gap-3 skeleton-pulse"
                  >
                    <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-zinc-800"></div>
                    <div className="h-4 w-24 sm:w-32 bg-gray-200 dark:bg-zinc-800 rounded"></div>
                    <div className="h-3 w-12 sm:w-16 bg-gray-200 dark:bg-zinc-800 rounded ml-auto"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

import { updateThreadAsShared } from "@/lib/utils/teamAccessUtils";

import { ChatInput } from "@/components";

import WorkspaceFiles from "@/components/workspace/WorkspaceFiles";


import WorkspaceSettingsDialog from "@/components/workspace/dialogs/WorkspaceSettingsDialog";

const FileTypeIcons = ({ files = [] }) => {
  const fileTypes = {};
  files.forEach((file) => {
    const type = file.file_type?.split("/")[1]?.toLowerCase() || "unknown";
    fileTypes[type] = (fileTypes[type] || 0) + 1;
  });

  const topTypes = Object.entries(fileTypes)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map((entry) => entry[0]);

  if (topTypes.length === 0) return null;

  return (
    <div className="flex items-center -space-x-2 sm:-space-x-3 mr-1">
      {topTypes.map((type, index) => {
        let Icon = FileText;
        let color = "text-blue-500";
        let bgColor = "bg-blue-100";
        let zIndex = "";

        if (type.includes("json")) {
          Icon = FileJson;
          color = "text-yellow-600";
          bgColor = "bg-yellow-100";
        } else if (type.includes("pdf")) {
          Icon = FileText;
          color = "text-red-600";
          bgColor = "bg-red-100";
        } else if (type.includes("text") || type.includes("txt")) {
          Icon = FileText;
          color = "text-blue-600";
          bgColor = "bg-blue-100";
        }

        if (index === 0) zIndex = "z-30";
        else if (index === 1) zIndex = "z-20";
        else zIndex = "z-10";

        return (
          <div
            key={type}
            className={`${bgColor} h-6 w-6 sm:h-8 sm:w-8 rounded-lg flex items-center justify-center border-2 border-gray-300 dark:border-zinc-900 ${zIndex}`}
          >
            <Icon className={`h-3 w-3 sm:h-4 sm:w-4 ${color}`} />
          </div>
        );
      })}
    </div>
  );
};

export default function SingleWorkspacePage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const [isAssistantDialogOpen, setIsAssistantDialogOpen] = useState(false);
  const [isFilesDialogOpen, setIsFilesDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isEditProjectDialogOpen, setIsEditProjectDialogOpen] = useState(false);
  const [isManageTeamDialogOpen, setIsManageTeamDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [isRenamingWorkspace, setIsRenamingWorkspace] = useState(false);
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const [manageTeamInitialTab, setManageTeamInitialTab] = useState("invitations");
  const [isLoading, setIsLoading] = useState(true);
  const [creatingChat, setCreatingChat] = useState(false);
  const [error, setError] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [threadDetails, setThreadDetails] = useState({});
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [workspaceAssistants, setWorkspaceAssistants] = useState([]);
  const [assistantsLoading, setAssistantsLoading] = useState(false);
  const [vectorStoreId, setVectorStoreId] = useState(null);
  const [isVectorStoreLoading, setIsVectorStoreLoading] = useState(false);

  const [files, setFiles] = useState(null);

  const isWorkspaceLoaded = useRef(false);

  const {
    user,
    setWorkspaces: setAuthWorkspaces,
    workspaces,
    setCurrentWorkspaceId,
  } = useAuthStore();

  const { createThread, clearCurrentThread } = useThreadStore();

  const { assistants } = useAssistantStore();

  useEffect(() => {
    if (workspaceId) {
      setCurrentWorkspaceId(workspaceId);
    }
  }, [workspaceId, setCurrentWorkspaceId]);

  // Sync local workspace state with authStore workspaces array
  // This ensures UI updates when workspace metadata is changed elsewhere (e.g., color/icon picker)
  useEffect(() => {
    if (!workspaceId || !workspaces?.length) return;
    const storeWorkspace = workspaces.find((w) => w.id === workspaceId);
    if (storeWorkspace) {
      setWorkspace((prevWorkspace) => {
        // Only update if there are actual changes to prevent unnecessary re-renders
        if (!prevWorkspace) return storeWorkspace;
        // Check if metadata changed
        const metadataChanged =
          JSON.stringify(prevWorkspace.metadata) !==
          JSON.stringify(storeWorkspace.metadata);
        const nameChanged = prevWorkspace.name !== storeWorkspace.name;
        if (metadataChanged || nameChanged) {
          return { ...prevWorkspace, ...storeWorkspace };
        }
        return prevWorkspace;
      });
    }
  }, [workspaceId, workspaces]);

  // Handle ?manageTeam=true query param from sidebar navigation
  useEffect(() => {
    if (searchParams.get("manageTeam") === "true") {
      setIsManageTeamDialogOpen(true);
      // Remove the query param after opening the dialog
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle ?dialog=X query param for deep-linking to dialogs
  useEffect(() => {
    const dialogParam = searchParams.get("dialog");
    if (dialogParam === "files") {
      setIsFilesDialogOpen(true);
    } else if (dialogParam === "assistants") {
      setIsAssistantDialogOpen(true);
    }
  }, [searchParams]);

  // Handler for dialog open/close that syncs with URL
  const handleDialogOpenChange = useCallback((dialogName, isOpen) => {
    if (isOpen) {
      setSearchParams({ dialog: dialogName }, { replace: true });
    } else {
      // Remove dialog param when closing
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("dialog");
      setSearchParams(newParams, { replace: true });
    }

    // Update the corresponding state
    if (dialogName === "files") {
      setIsFilesDialogOpen(isOpen);
    } else if (dialogName === "assistants") {
      setIsAssistantDialogOpen(isOpen);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let isMounted = true;

    const loadWorkspace = async () => {
      if (!workspaceId || !user?.id) return;

      if (isWorkspaceLoaded.current && workspace?.id === workspaceId) {
        return;
      }

      try {
        if (isMounted) {
          setIsLoading(true);
          setError(null);
        }

        if (isMounted) {
          setThreadDetails({});
          setLoadingThreads(true);
        }

        const workspaceData =
          await SupabaseWorkspaceUtils.getWorkspace(workspaceId);

        if (!workspaceData) {
          throw new Error("Workspace not found");
        }

        if (isMounted) {
          setWorkspace(workspaceData);
          isWorkspaceLoaded.current = true;

          useAuthStore.setState({
            currentWorkspace: workspaceData,
            currentWorkspaceId: workspaceData.id,
          });

          // CRITICAL: Also ensure workspace context is available for streaming
          // This ensures the workspace data has all necessary fields for instruction building
          const workspaceContextData = {
            id: workspaceData.id,
            name: workspaceData.name || "",
            description: workspaceData.description || "",
            instructions: workspaceData.instructions || "",
            include_workspace_instructions:
              workspaceData.include_workspace_instructions !== false, // Default to true
            include_profile_context:
              workspaceData.include_profile_context || false,
            vector_store_id: workspaceData.vector_store_id || null,
            is_team: workspaceData.is_team || false,
          };

          // Store the enhanced workspace data
          useAuthStore.setState({
            currentWorkspace: {
              ...workspaceData,
              ...workspaceContextData,
            },
          });

          // Clear any stale localStorage items that might cause automatic navigation
          // This prevents issues when navigating to a workspace after creating it
          localStorage.removeItem("pendingWorkspaceMessage");
          localStorage.removeItem("pendingWorkspaceChat");
          localStorage.removeItem("pendingWorkspaceType");
          localStorage.removeItem("pendingWorkspaceFiles");
          localStorage.removeItem("pendingVisionImageUrls");
          localStorage.removeItem("pendingFileMetadata");
          localStorage.removeItem("pendingThreadTitle");
          localStorage.removeItem("createSupabaseOnlyThread");
          localStorage.removeItem("forceNewThread");
          localStorage.removeItem("clearMessagesAndContext");
          localStorage.removeItem("preserveUserMessages");
          localStorage.removeItem("needs_title_generation");
          localStorage.removeItem("pending_thread_title");
          localStorage.removeItem("pendingWorkspaceVectorStoreId");

          if (
            workspaceData.is_team &&
            (workspaceData.created_now || workspaceData.metadata?.created_now)
          ) {
            // Open ManageTeamDialog on invitations tab for newly created team workspaces
            setManageTeamInitialTab("invitations");
            setIsManageTeamDialogOpen(true);

            await supabase
              .from("workspaces")
              .update({
                created_now: false,
                metadata: {
                  ...workspaceData.metadata,
                  created_now: false,
                },
              })
              .eq("id", workspaceData.id);
          }
        }

        if (workspaceData.vector_store_id) {
          if (isMounted) {
            setVectorStoreId(workspaceData.vector_store_id);
          }
        } else {
          await createVectorStoreForWorkspace(
            workspaceData.id,
            workspaceData.name
          );
        }

        if (
          !workspaceData.workspace_threads ||
          workspaceData.workspace_threads.length === 0
        ) {
          if (isMounted) {
            setLoadingThreads(false);
          }
        }

        if (workspaces && Array.isArray(workspaces)) {
          const updatedWorkspaces = workspaces.map((w) =>
            w.id === workspaceId ? workspaceData : w
          );
          setAuthWorkspaces(updatedWorkspaces);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          setLoadingThreads(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWorkspace();

    return () => {
      isMounted = false;
      isWorkspaceLoaded.current = false;
    };
  }, [workspaceId, user?.id]);

  // Simplified assistant loading for workspace header only
  useEffect(() => {
    const loadWorkspaceAssistants = async () => {
      if (!workspace || !workspace.workspace_assistants) {
        setWorkspaceAssistants([]);
        setAssistantsLoading(false);
        return;
      }

      setAssistantsLoading(true);
      try {
        if (workspace.workspace_assistants.length > 0) {
          const { data: fetchedAssistants, error } = await supabase
            .from("assistants")
            .select("*")
            .in("id", workspace.workspace_assistants);

          if (error) {
            setWorkspaceAssistants([]);
            return;
          }

          if (fetchedAssistants && fetchedAssistants.length > 0) {
            const assistantsWithTools = fetchedAssistants.map((assistant) =>
              useAssistantStore.getState().reconstructAssistantTools(assistant)
            );
            setWorkspaceAssistants(assistantsWithTools);
          } else {
            setWorkspaceAssistants([]);
          }
        } else {
          setWorkspaceAssistants([]);
        }
      } catch (error) {
        console.error("Error loading workspace assistants:", error);
        setWorkspaceAssistants([]);
      } finally {
        setAssistantsLoading(false);
      }
    };

    loadWorkspaceAssistants();
  }, [workspace?.workspace_assistants]);

  useEffect(() => {
    if (workspace) {
      if (workspace.workspace_threads?.length > 0) {
        fetchThreadDetails(workspace.workspace_threads);
      } else {
        setLoadingThreads(false);
      }
    }
  }, [workspace?.workspace_threads]);

  // Listen for thread move events to refresh workspace threads
  useEffect(() => {
    const handleThreadMoved = (event) => {
      const { targetWorkspaceId, sourceWorkspaceId } = event.detail;

      // If a thread was moved to or from this workspace, sync from authStore
      if (targetWorkspaceId === workspaceId || sourceWorkspaceId === workspaceId) {
        // Get the updated workspace from authStore
        const authWorkspaces = useAuthStore.getState().workspaces;
        const updatedWorkspace = authWorkspaces.find((w) => w.id === workspaceId);

        if (updatedWorkspace) {
          // Update local workspace state to trigger thread refresh
          setWorkspace((prev) => ({
            ...prev,
            workspace_threads: updatedWorkspace.workspace_threads || [],
          }));
        }
      }
    };

    window.addEventListener("workspace-thread-moved", handleThreadMoved);
    return () => {
      window.removeEventListener("workspace-thread-moved", handleThreadMoved);
    };
  }, [workspaceId]);

  useEffect(() => {
    const fetchWorkspaceFiles = async () => {
      if (!workspaceId) return;

      try {
        const { data: workspace, error: workspaceError } = await supabase
          .from("workspaces")
          .select("workspace_files")
          .eq("id", workspaceId)
          .single();

        if (workspaceError) throw workspaceError;

        if (
          !workspace.workspace_files ||
          workspace.workspace_files.length === 0
        ) {
          setFiles([]);
          return;
        }

        const { data: fileData, error } = await supabase
          .from("workspace_files")
          .select("*")
          .in("id", workspace.workspace_files)
          .order("created_at", { ascending: false });

        if (error) throw error;
        setFiles(fileData || []);
      } catch (err) {
        console.error("Error loading workspace files:", err);
        setFiles([]);
      }
    };

    if (workspace) {
      fetchWorkspaceFiles();
    }
  }, [workspaceId, workspace]);

  const createVectorStoreForWorkspace = async (workspaceId, workspaceName) => {
    try {
      setIsVectorStoreLoading(true);

      if (!chatCompletionService) {
        throw new Error("Chat completion service is not properly configured");
      }

      let vectorStoreResponse;

      try {
        // Use the ChatCompletionService to create vector store
        vectorStoreResponse = await chatCompletionService.createVectorStore(
          `${workspaceName} Vector Store`
        );
      } catch (apiError) {
        throw new Error(`Failed to create vector store: ${apiError.message}`);
      }

      if (!vectorStoreResponse || !vectorStoreResponse.id) {
        throw new Error(
          "Failed to create vector store: no valid response received"
        );
      }

      const { data: updatedWorkspace, error } = await supabase
        .from("workspaces")
        .update({
          vector_store_id: vectorStoreResponse.id,
          metadata: {
            ...workspace?.metadata,
            vector_store_created_at: new Date().toISOString(),
          },
        })
        .eq("id", workspaceId)
        .select()
        .single();

      if (error) throw error;

      setVectorStoreId(vectorStoreResponse.id);
      setWorkspace(updatedWorkspace);

      // CRITICAL FIX: Update the auth store with the new vector store ID
      const authStore = useAuthStore.getState();

      // Update the current workspace in auth store if it matches
      if (authStore.currentWorkspaceId === workspaceId) {
        authStore.setCurrentWorkspace({
          ...updatedWorkspace,
          vector_store_id: vectorStoreResponse.id,
        });
      }

      // Update the workspaces array in auth store
      const updatedWorkspaces = workspaces.map((w) =>
        w.id === workspaceId ? updatedWorkspace : w
      );
      setAuthWorkspaces(updatedWorkspaces);

      return vectorStoreResponse.id;
    } catch (err) {
      setError(`Error creating vector store: ${err.message}`);
      return null;
    } finally {
      setIsVectorStoreLoading(false);
    }
  };

  const fetchThreadDetails = async (threadIds) => {
    setLoadingThreads(true);
    try {
      if (!threadIds || threadIds.length === 0) {
        setThreadDetails({});
        return;
      }

      const threadDetailsMap = {};

      await Promise.all(
        threadIds.map(async (threadId) => {
          try {
            const threadStore = useThreadStore.getState();
            const storedThread = threadStore.threads.find(
              (t) => t.id === threadId
            );

            if (storedThread) {
              threadDetailsMap[threadId] = {
                title: storedThread.title || "Untitled Chat",
                lastMessageAt:
                  storedThread.last_message_at || storedThread.updated_at,
                id: threadId,
                assistant: storedThread.assistant,
              };
            } else {
              const { data: threadData, error } = await supabase
                .from("threads")
                .select("title, last_message_at, updated_at, id, assistant_id")
                .eq("id", threadId)
                .maybeSingle();

              if (error) {
                threadDetailsMap[threadId] = {
                  title: "Untitled Chat",
                  lastMessageAt: new Date().toISOString(),
                  id: threadId,
                };
                return;
              }

              if (!threadData) {
                threadDetailsMap[threadId] = {
                  title: "Untitled Chat",
                  lastMessageAt: new Date().toISOString(),
                  id: threadId,
                };
                return;
              }

              threadDetailsMap[threadId] = {
                title: threadData.title || "Untitled Chat",
                lastMessageAt:
                  threadData.last_message_at || threadData.updated_at,
                id: threadId,
              };
            }
          } catch (err) {
            threadDetailsMap[threadId] = {
              title: "Untitled Chat",
              lastMessageAt: new Date().toISOString(),
              id: threadId,
            };
          }
        })
      );

      setThreadDetails(threadDetailsMap);
    } catch (err) {
      setThreadDetails({});
    } finally {
      setLoadingThreads(false);
    }
  };

  const handleAssistantAction = async (assistantId, action) => {
    try {
      setIsLoading(true);
      setError(null);

      const assistantIdStr = String(assistantId);
      const workspaceIdStr = String(workspaceId);

      const assistant = assistants.find((a) => a.id === assistantIdStr);
      if (!assistant) {
        console.error("❌ Assistant not found:", {
          assistantIdStr,
          availableIds: assistants.map((a) => a.id),
        });
        throw new Error("Assistant not found");
      }

      if (!assistant?.name) {
        assistant.name = "Unnamed Assistant";
      }

      if (action === "attach") {
        try {
          const { addWorkspaceToAssistant } = await import(
            "@/lib/db/utils/supabase/assistants"
          );

          await addWorkspaceToAssistant(assistantIdStr, workspaceIdStr);

          await SupabaseWorkspaceUtils.addAssistantToWorkspace(
            workspaceIdStr,
            assistantIdStr
          );

          const updatedWorkspace =
            await SupabaseWorkspaceUtils.getWorkspace(workspaceIdStr);

          setWorkspace(updatedWorkspace);

          setAuthWorkspaces(
            workspaces.map((w) =>
              w.id === workspaceIdStr ? updatedWorkspace : w
            )
          );

          if (useAuthStore.getState().currentWorkspaceId === workspaceIdStr) {
            useAuthStore.setState({
              currentWorkspace: updatedWorkspace,
            });
          }

          const assistantUpdate = {
            ...assistant,
            workspace_ids: [
              ...(assistant.workspace_ids || []),
              workspaceIdStr,
            ].filter((value, index, self) => self.indexOf(value) === index),
          };

          setWorkspaceAssistants((prev) => {
            const exists = prev.some((a) => String(a.id) === assistantIdStr);
            let newState;
            if (exists) {
              newState = prev.map((a) =>
                String(a.id) === assistantIdStr ? assistantUpdate : a
              );
            } else {
              newState = [...prev, assistantUpdate];
            }
            return newState;
          });
        } catch (assistantError) {
          console.error("❌ Error attaching assistant:", assistantError);
          throw new Error(
            `Failed to attach assistant: ${assistantError.message}`
          );
        }
      } else if (action === "detach") {
        const { removeWorkspaceFromAssistant } = await import(
          "@/lib/db/utils/supabase/assistants"
        );

        await removeWorkspaceFromAssistant(assistantIdStr, workspaceIdStr);
        await SupabaseWorkspaceUtils.removeAssistantFromWorkspace(
          workspaceIdStr,
          assistantIdStr
        );

        const updatedWorkspace =
          await SupabaseWorkspaceUtils.getWorkspace(workspaceIdStr);

        setWorkspace(updatedWorkspace);

        setAuthWorkspaces(
          workspaces.map((w) =>
            w.id === workspaceIdStr ? updatedWorkspace : w
          )
        );

        if (useAuthStore.getState().currentWorkspaceId === workspaceIdStr) {
          useAuthStore.setState({
            currentWorkspace: updatedWorkspace,
          });
        }

        setWorkspaceAssistants((prev) =>
          prev.filter((a) => String(a.id) !== assistantIdStr)
        );

        const assistantStore = useAssistantStore.getState();
        const updatedAssistants = assistantStore.assistants.map((a) =>
          String(a.id) === assistantIdStr
            ? {
                ...a,
                workspace_ids: (a.workspace_ids || []).filter(
                  (id) => String(id) !== workspaceIdStr
                ),
              }
            : a
        );

        const updatedAssistant = updatedAssistants.find(
          (a) => String(a.id) === assistantIdStr
        );
        if (updatedAssistant) {
          await assistantStore.updateAssistant(
            assistantIdStr,
            updatedAssistant
          );
        }
      }
    } catch (err) {
      console.error(
        `❌ Error ${action === "attach" ? "attaching" : "detaching"} assistant:`,
        err
      );
      console.error("❌ Full error details:", {
        message: err.message,
        stack: err.stack,
        assistantId,
        action,
        workspaceId,
      });
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateChat = async () => {
    try {
      setCreatingChat(true);
      setError(null);

      const userId = String(user?.id || "");
      const workspaceIdStr = String(workspaceId || "");
      const pendingWorkspaceContent = localStorage.getItem(
        "pendingWorkspaceMessage"
      );

      if (!userId) {
        throw new Error("User ID is required to create a chat");
      }

      if (!workspaceIdStr) {
        throw new Error("Workspace ID is required to create a chat");
      }

      if (!workspace) {
        throw new Error("Workspace data is required to create a chat");
      }

      const isTeamWorkspace = workspace.is_team === true;

      // Get the current optimistic message from thread store
      const currentThreadState = useThreadStore.getState();
      const optimisticMessage =
        currentThreadState.pendingWorkspaceMessage ||
        currentThreadState.messages?.find(
          (m) => m.metadata?.isOptimistic === true
        );

      const threadMetadata = {
        user_id: userId,
        workspace_id: workspaceIdStr,
        created_at: new Date().toISOString(),
        workspace_name: workspace?.name || "Workspace",
        is_shared_thread: isTeamWorkspace,
        shared_in_workspace: isTeamWorkspace ? workspaceIdStr : null,
        needs_title_generation: true,
        is_team: isTeamWorkspace,
        from_workspace_chat: true,
        workspace_initiated: true,
      };

      const thread = await createThread(
        userId,
        null,
        pendingWorkspaceContent
          ? "Workspace chat"
          : `New chat in ${workspace.name || "Workspace"}`, // temporary title
        null,
        threadMetadata,
        {
          markAsShared: isTeamWorkspace,
          workspaceId: isTeamWorkspace ? workspaceIdStr : null,
        }
      );

      if (!thread) {
        throw new Error("Failed to create thread - no thread returned");
      }

      if (!thread.id) {
        throw new Error("Failed to create thread - no thread ID returned");
      }

      // Update the optimistic message with the thread ID
      if (optimisticMessage) {
        const updatedOptimisticMessage = {
          ...optimisticMessage,
          thread_id: thread.id,
          threadId: thread.id,
        };

        // Update thread store with the thread-linked optimistic message
        useThreadStore.setState((state) => ({
          ...state,
          pendingWorkspaceMessage: updatedOptimisticMessage,
          preventNewMessageClear: true,
          currentThreadId: thread.id,
          currentThread: thread,
        }));
      }

      // CRITICAL: Set up title generation flags for the new thread
      const pendingMessage = localStorage.getItem("pendingWorkspaceMessage");
      if (pendingMessage) {
        // localStorage.setItem(`generate_title_for_${thread.id}`, "true");
        // localStorage.setItem(`title_content_for_${thread.id}`, pendingMessage);
        localStorage.setItem("needs_title_generation", "true");
      }

      if (pendingWorkspaceContent) {
        try {
          const { enqueueTitleGeneration } = useThreadStore.getState();
          enqueueTitleGeneration(thread.id, pendingWorkspaceContent);
        } catch (titleError) {
          console.error(
            "Error queueing workspace title generation:",
            titleError
          );
        }
      }

      await SupabaseWorkspaceUtils.addThreadToWorkspace(
        workspaceIdStr,
        String(thread.id)
      );

      if (isTeamWorkspace && !thread.metadata?.is_shared_thread) {
        try {
          await updateThreadAsShared(thread.id, workspaceIdStr);
        } catch (shareError) {
          console.error("Error marking thread as shared:", shareError);
        }
      }

      const updatedWorkspace =
        await SupabaseWorkspaceUtils.getWorkspace(workspaceIdStr);

      if (!updatedWorkspace) {
        console.warn(
          "Failed to fetch updated workspace data after thread creation"
        );
      } else {
        setWorkspace(updatedWorkspace);
        setAuthWorkspaces(
          workspaces.map((w) =>
            w.id === workspaceIdStr ? updatedWorkspace : w
          )
        );
      }

      navigate(`/chat/${thread.id}`);
    } catch (err) {
      console.error("Error creating chat with workspace:", err);
      setError(err.message || "An error occurred while creating chat");
    } finally {
      setCreatingChat(false);
    }
  };

  const handleWorkspaceChat = (messageData) => {
    // CRITICAL FIX: Instead of clearing ALL messages, preserve any user messages that might exist
    // and only clear assistant/system messages to prevent conflicts
    const threadStore = useThreadStore.getState();
    const currentMessages = threadStore.messages || [];

    // Preserve user messages but clear assistant/system messages to start fresh
    const preservedUserMessages = currentMessages.filter(
      (msg) => msg.role === "user"
    );

    // Only clear if we have assistant/system messages to clear
    if (currentMessages.some((msg) => msg.role !== "user")) {
      threadStore.setMessages(preservedUserMessages);
    }

    // Also clear any current thread state but preserve the user messages
    clearCurrentThread();

    // If we preserved user messages, restore them after clearing thread
    if (preservedUserMessages.length > 0) {
      setTimeout(() => {
        const currentState = useThreadStore.getState();
        if (!currentState.messages || currentState.messages.length === 0) {
          threadStore.setMessages(preservedUserMessages);
        }
      }, 100);
    }

    // Store the message content in localStorage for the chat page to retrieve
    if (messageData && messageData.content) {
      localStorage.setItem("pendingWorkspaceMessage", messageData.content);

      // CRITICAL: Set explicit title generation flags to match regular chat flow
      // This ensures title generation works for workspace-initiated chats
      localStorage.setItem("needs_title_generation", "true");
      localStorage.setItem(
        "pending_thread_title",
        JSON.stringify({
          content: messageData.content,
          timestamp: Date.now(),
          retries: 0,
        })
      );
    }

    // CRITICAL FIX: Handle files and images for vision processing
    if (
      messageData &&
      (messageData.files ||
        messageData.storageFiles ||
        messageData.fileMetadata)
    ) {
      // Use fileMetadata as the primary source since it has all the processed properties
      // Fall back to storageFiles or files if fileMetadata is not available
      const filesToStore =
        messageData.fileMetadata ||
        messageData.storageFiles ||
        messageData.files ||
        [];

      // Process files to ensure they have proper vision metadata
      const processedFiles = filesToStore.map((file) => {
        const isImage = file.isImage || file.type?.startsWith("image/");

        // Ensure vision metadata is properly set for images
        if (isImage) {
          const visionMetadata = file.visionMetadata || {
            publicUrl: file.publicUrl || file.url || file.data?.publicUrl,
            contentType: file.type,
            isReady: true,
          };

          return {
            ...file,
            isImage: true,
            needsVision: true,
            visionMetadata,
            // Ensure all URL variants are available
            publicUrl: file.publicUrl || file.url || file.data?.publicUrl,
            url: file.url || file.publicUrl || file.data?.publicUrl,
          };
        }

        return file;
      });

      // Store processed files in localStorage
      localStorage.setItem(
        "pendingWorkspaceFiles",
        JSON.stringify(processedFiles)
      );

      // Extract vision image URLs for immediate use
      const visionImageUrls = processedFiles
        .filter((file) => file.isImage && (file.publicUrl || file.url))
        .map((file) => file.publicUrl || file.url)
        .filter(Boolean);

      if (visionImageUrls.length > 0) {
        localStorage.setItem(
          "pendingVisionImageUrls",
          JSON.stringify(visionImageUrls)
        );
      }

      // Store file metadata for context
      localStorage.setItem(
        "pendingFileMetadata",
        JSON.stringify({
          hasFiles: processedFiles.length > 0,
          hasImages: processedFiles.some((f) => f.isImage),
          imageCount: processedFiles.filter((f) => f.isImage).length,
          fileCount: processedFiles.length,
          visionImageUrls: visionImageUrls,
        })
      );
    }

    // Store workspace context in localStorage to ensure it's available after navigation
    localStorage.setItem("pendingWorkspaceChat", workspaceId);

    // Store workspace type to help thread creation logic determine is_team flag
    localStorage.setItem(
      "pendingWorkspaceType",
      workspace?.is_team ? "team" : "personal"
    );

    // Add flags needed for workspace chat creation
    localStorage.setItem("createSupabaseOnlyThread", "true");
    localStorage.setItem("forceNewThread", "true");
    // CRITICAL FIX: Don't set clearMessagesAndContext to prevent aggressive clearing
    localStorage.setItem("preserveUserMessages", "true");
    localStorage.setItem("needs_title_generation", "true");

    // Add the vector store ID to the context data for retrieval-augmented generation
    // CRITICAL FIX: Get the vector store ID from auth store's currentWorkspace to ensure it's always current
    const currentAuthWorkspace = useAuthStore.getState().currentWorkspace;
    const effectiveVectorStoreId =
      currentAuthWorkspace?.vector_store_id || vectorStoreId;

    if (effectiveVectorStoreId) {
      // Add vector store ID to the message data if messageData is an object
      if (messageData && typeof messageData === "object") {
        // Ensure vector_store_id is a string, not an object
        const vectorStoreIdString =
          typeof effectiveVectorStoreId === "string"
            ? effectiveVectorStoreId
            : effectiveVectorStoreId?.id || String(effectiveVectorStoreId);

        messageData.contextData = {
          ...(messageData.contextData || {}),
          vector_store_id: vectorStoreIdString,
          workspace_vector_store_id: vectorStoreIdString,
          workspace_id: workspaceId,
          is_workspace_chat: true,
          retrieve_context: true,
          enable_retrieval: true,
          from_workspace_chat: true,
          workspace_initiated: true,
          // Add vision-specific context if we have images
          ...(messageData.files?.some((f) => f.isImage) ||
          messageData.storageFiles?.some((f) => f.isImage)
            ? {
                has_vision_enabled: true,
                has_images: true,
                vision_image_urls: JSON.parse(
                  localStorage.getItem("pendingVisionImageUrls") || "[]"
                ),
              }
            : {}),
        };

        // Log current workspace in auth store
        console.log(
          "Using vector store ID for workspace chat:",
          vectorStoreIdString
        );
        console.log(
          "Auth workspace vector store ID:",
          currentAuthWorkspace?.vector_store_id
        );

        // CRITICAL: Store the vector store ID in localStorage for the chat page to retrieve
        localStorage.setItem(
          "pendingWorkspaceVectorStoreId",
          vectorStoreIdString
        );
      }

      // SIMPLIFIED: Vector store management is now handled by thread store
      // No need to store in localStorage
    } else {
      // Log warning if no vector store ID is available
      console.warn("No vector store ID available for workspace chat");
    }

    // NEW: Create optimistic user message and add it to thread store immediately
    const optimisticUserMessage = {
      id: fileUtils.generateUUID(),
      message_id: fileUtils.generateUUID(),
      thread_id: null, // Will be set when thread is created
      workspace_id: workspaceId,
      role: "user",
      content: messageData.content,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      metadata: {
        isOptimistic: true,
        from_workspace_chat: true,
        workspace_initiated: true,
        ...(messageData.files?.length > 0 && {
          has_files: true,
          file_count: messageData.files.length,
        }),
        ...(messageData.files?.some((f) => f.isImage) && {
          has_images: true,
          image_count: messageData.files.filter((f) => f.isImage).length,
        }),
      },
      attachments:
        messageData.files?.map((f) => ({
          id: f.file_id || f.openai_file_id || f.id,
          name: f.name || f.path?.split("/").pop() || "file",
          type: f.type,
          path: f.path || f.storage_path,
          openai_file_id: f.openai_file_id || f.file_id,
        })) || [],
    };

    // Store the optimistic message in localStorage for persistence
    localStorage.setItem(
      "optimisticWorkspaceMessage",
      JSON.stringify(optimisticUserMessage)
    );

    // Store the optimistic message in a way that won't interfere with normal message flow
    // We'll use a special flag to indicate this is a pending workspace message
    useThreadStore.setState((state) => ({
      ...state,
      pendingWorkspaceMessage: optimisticUserMessage,
      preventNewMessageClear: true,
      // Don't add to messages array yet - let the chat context handle it properly
    }));

    // Call the existing chat creation function
    handleCreateChat();
  };

  // Handle creating a chat with a specific assistant
  const handleCreateChatWithAssistant = async (assistantId) => {
    try {
      setCreatingChat(true);
      setError(null);

      // Ensure all IDs are strings
      const userId = String(user?.id || "");
      const workspaceIdStr = String(workspaceId || "");
      const assistantIdStr = String(assistantId || "");

      if (!userId || !workspaceIdStr || !assistantIdStr) {
        throw new Error("Missing required IDs for chat creation");
      }

      if (!workspace) {
        throw new Error("Workspace data is required to create a chat");
      }

      // Find the assistant to get its name
      const assistant = workspaceAssistants.find(
        (a) => a.id === assistantIdStr
      );

      if (!assistant) {
        throw new Error("Selected assistant not found");
      }

      // Prepare metadata with stringified values
      const threadMetadata = {
        user_id: userId,
        workspace_id: workspaceIdStr,
        assistant_id: assistantIdStr,
        created_at: new Date().toISOString(),
        workspace_name: workspace?.name || "Workspace",
        // Add flag for title regeneration
        needs_title_generation: true,
        // CRITICAL: Set is_team flag based on workspace type
        is_team: workspace.is_team === true,
        is_shared_thread: workspace.is_team === true,
        shared_in_workspace: workspace.is_team === true ? workspaceIdStr : null,
      };

      // Create a new thread with the specified assistant and a temporary title
      const thread = await createThread(
        userId,
        assistantIdStr,
        `New chat with ${assistant.name || "Assistant"}`,
        null,
        threadMetadata
      );

      if (!thread || !thread.id) {
        throw new Error("Failed to create thread");
      }

      // Add the thread to the workspace
      await SupabaseWorkspaceUtils.addThreadToWorkspace(
        workspaceIdStr,
        String(thread.id)
      );

      // Update workspace in local state
      const updatedWorkspace =
        await SupabaseWorkspaceUtils.getWorkspace(workspaceIdStr);
      if (updatedWorkspace) {
        setWorkspace(updatedWorkspace);
        setAuthWorkspaces(
          workspaces.map((w) =>
            w.id === workspaceIdStr ? updatedWorkspace : w
          )
        );
      }

      // Navigate to the new chat
      navigate(`/chat/${thread.id}`);
    } catch (err) {
      console.error("Error creating chat with assistant:", err);
    } finally {
      setCreatingChat(false);
    }
  };

  // Handle invitations being sent
  const handleInvitationsSent = (invitedUsers) => {
    // Update the workspace with the latest data
    SupabaseWorkspaceUtils.getWorkspace(workspaceId)
      .then((updatedWorkspace) => {
        if (updatedWorkspace) {
          setWorkspace(updatedWorkspace);
        }
      })
      .catch((err) => console.error("Error refreshing workspace data:", err));
  };


  // Handle workspace settings updates
  const handleWorkspaceUpdate = async (updatedData) => {
    try {
      // Update the workspace in the database
      const { data: updatedWorkspace, error } = await supabase
        .from("workspaces")
        .update(updatedData)
        .eq("id", workspaceId)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setWorkspace(updatedWorkspace);

      // Update auth store workspaces
      setAuthWorkspaces(
        workspaces.map((w) => (w.id === workspaceId ? updatedWorkspace : w))
      );

      // Also update current workspace in auth store if it's the current one
      if (useAuthStore.getState().currentWorkspaceId === workspaceId) {
        useAuthStore.setState({
          currentWorkspace: updatedWorkspace,
        });

        // CRITICAL: Ensure workspace context has all necessary fields for instruction building
        const workspaceContextData = {
          id: updatedWorkspace.id,
          name: updatedWorkspace.name || "",
          description: updatedWorkspace.description || "",
          instructions: updatedWorkspace.instructions || "",
          include_workspace_instructions:
            updatedWorkspace.include_workspace_instructions !== false, // Default to true
          include_profile_context:
            updatedWorkspace.include_profile_context || false,
          vector_store_id: updatedWorkspace.vector_store_id || null,
          is_team: updatedWorkspace.is_team || false,
        };

        // Store the enhanced workspace data
        useAuthStore.setState({
          currentWorkspace: {
            ...updatedWorkspace,
            ...workspaceContextData,
          },
        });
      }

      // Close the dialog
      setIsSettingsDialogOpen(false);
    } catch (err) {
      console.error("Error updating workspace:", err);
    }
  };

  // Handle workspace deletion
  const handleWorkspaceDeletion = async () => {
    try {
      setIsDeletingWorkspace(true);
      // Delete the workspace from the database
      const { error } = await supabase
        .from("workspaces")
        .delete()
        .eq("id", workspaceId);

      if (error) throw error;

      // Update auth store by removing the workspace
      setAuthWorkspaces(workspaces.filter((w) => w.id !== workspaceId));

      toast({
        title: "Project deleted",
        description: "The project has been deleted successfully.",
      });

      // Navigate to workspaces page
      navigate("/workspaces");
    } catch (err) {
      console.error("Error deleting workspace:", err);
      toast({
        title: "Error",
        description: "Failed to delete project.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingWorkspace(false);
      setIsDeleteDialogOpen(false);
    }
  };

  // Handle opening rename dialog
  const handleOpenRenameDialog = () => {
    setNewWorkspaceName(workspace?.name || "");
    setIsRenameDialogOpen(true);
  };

  // Handle workspace rename
  const handleWorkspaceRename = async () => {
    if (!newWorkspaceName.trim()) return;

    try {
      setIsRenamingWorkspace(true);
      const { error } = await supabase
        .from("workspaces")
        .update({
          name: newWorkspaceName.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", workspaceId);

      if (error) throw error;

      // Update local state
      setWorkspace((prev) => ({ ...prev, name: newWorkspaceName.trim() }));
      setAuthWorkspaces(
        workspaces.map((w) =>
          w.id === workspaceId ? { ...w, name: newWorkspaceName.trim() } : w
        )
      );

      toast({
        title: "Project renamed",
        description: "The project name has been updated.",
      });

      setIsRenameDialogOpen(false);
    } catch (err) {
      console.error("Error renaming workspace:", err);
      toast({
        title: "Error",
        description: "Failed to rename project.",
        variant: "destructive",
      });
    } finally {
      setIsRenamingWorkspace(false);
    }
  };

  // Handle opening delete dialog
  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  // Check if current user is the owner
  const isOwner = workspace?.owner_id === user?.id;

  // Handle archive project
  const handleArchiveProject = async () => {
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({
          archived_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", workspaceId);

      if (error) throw error;

      toast({
        title: "Project archived",
        description: "This project has been archived.",
      });

      // Update local state
      setWorkspace((prev) => ({ ...prev, archived_at: new Date().toISOString() }));
      setAuthWorkspaces(
        workspaces.map((w) =>
          w.id === workspaceId
            ? { ...w, archived_at: new Date().toISOString() }
            : w
        )
      );
    } catch (err) {
      console.error("Error archiving project:", err);
      toast({
        title: "Error",
        description: "Failed to archive project.",
        variant: "destructive",
      });
    }
  };

  // Handle duplicate project
  const handleDuplicateProject = async () => {
    try {
      const newWorkspace = {
        name: `${workspace.name} (Copy)`,
        description: workspace.description,
        instructions: workspace.instructions,
        icon: workspace.icon,
        color: workspace.color,
        is_team: false,
        owner_id: user.id,
        workspace_members: [user.id],
        include_workspace_instructions: workspace.include_workspace_instructions,
        include_profile_context: workspace.include_profile_context,
        workspace_assistants: workspace.workspace_assistants || [],
      };

      const { data, error } = await supabase
        .from("workspaces")
        .insert(newWorkspace)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Project duplicated",
        description: "A copy of this project has been created.",
      });

      // Add to workspaces list
      setAuthWorkspaces([...workspaces, data]);

      // Navigate to the new workspace
      navigate(`/projects/${data.id}`);
    } catch (err) {
      console.error("Error duplicating project:", err);
      toast({
        title: "Error",
        description: "Failed to duplicate project.",
        variant: "destructive",
      });
    }
  };

  // Handle leave project (for team members)
  const handleLeaveProject = async () => {
    if (isOwner) return;

    const confirmLeave = window.confirm(
      "Are you sure you want to leave this project?"
    );
    if (!confirmLeave) return;

    try {
      // Remove from workspace_members table
      const { error: memberError } = await supabase
        .from("workspace_members")
        .delete()
        .eq("workspace_id", workspaceId)
        .eq("user_id", user.id);

      if (memberError) throw memberError;

      // Remove from workspace_members array
      const { error: wsError } = await supabase
        .from("workspaces")
        .update({
          workspace_members: workspace.workspace_members?.filter(
            (id) => id !== user.id
          ),
        })
        .eq("id", workspaceId);

      if (wsError) throw wsError;

      toast({
        title: "Left project",
        description: "You have left this project.",
      });

      // Update workspaces list and navigate away
      setAuthWorkspaces(workspaces.filter((w) => w.id !== workspaceId));
      navigate("/workspaces");
    } catch (err) {
      console.error("Error leaving project:", err);
      toast({
        title: "Error",
        description: "Failed to leave project.",
        variant: "destructive",
      });
    }
  };

  // Handle open manage team dialog
  const handleOpenManageTeam = (tab = "invitations") => {
    setManageTeamInitialTab(tab);
    setIsManageTeamDialogOpen(true);
  };

  // Handle open edit project dialog
  const handleOpenEditProject = () => {
    setIsEditProjectDialogOpen(true);
  };

  // Show loading state
  if (isLoading && !workspace) {
    return (
      <>
        <WorkspacePageSkeleton />
        <SkeletonStyles />
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-transparent items-center justify-center">
        <div className="text-red-500 dark:text-red-400">Error: {error}</div>
      </div>
    );
  }

  // Show not found state
  if (!workspace) {
    return (
      <div className="flex flex-col min-h-screen bg-transparent items-center justify-center">
        <div className="text-gray-500 dark:text-zinc-400">
          Workspace not found
        </div>
      </div>
    );
  }

  return (
    <>
      <SkeletonStyles />
      <div className="flex flex-col min-h-screen bg-transparent">
        <div className="flex-1 container mx-auto px-4 sm:px-4 lg:px-6 py-4 sm:py-6 pb-24 overflow-y-auto">
          {/* Workspace Header */}
          <WorkspaceHeader
            workspace={workspace}
            vectorStoreId={vectorStoreId}
            onUpdateWorkspace={handleWorkspaceUpdate}
          />

          <div className="space-y-2 sm:space-y-3">
            {/* Chat Input for Workspace */}
            <div className="w-full mb-4 sm:mb-6 mt-16 sm:mt-16 flex justify-center">
              <div className="w-full sm:w-3/4 max-w-4xl px-0">
                <ChatInput
                  key={workspaceId}
                  onSendMessage={handleWorkspaceChat}
                  placeholder="Ask Anything"
                  acceptedFileTypes=".pdf,.doc,.docx,.txt,.json,.csv,.md"
                  maxFiles={5}
                  isNewChat={false}
                  autoFocus={true}
                  className="w-full"
                  isSplitView={true}
                />
              </div>
            </div>

            {/* Feature Cards - Files, Assistants */}
            <div className="w-full mb-4 sm:mb-6 flex justify-center">
              <div className="w-full sm:w-3/4 max-w-4xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 rounded-2xl border border-gray-200 dark:border-zinc-800 overflow-hidden">
                  {/* Files card */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-zinc-800"
                    onClick={() => handleDialogOpenChange("files", true)}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-400" />
                      <span className="font-medium text-gray-800 dark:text-white">
                        Files
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-zinc-400 mt-1">
                      {formatCount(files?.length ?? null, "file", "files")}
                    </div>
                  </div>
                  {/* Assistants card */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors"
                    onClick={() => handleDialogOpenChange("assistants", true)}
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-purple-400" />
                      <span className="font-medium text-gray-800 dark:text-white">
                        Assistants
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-zinc-400 mt-1">
                      {formatCount(workspaceAssistants?.length ?? null, "assistant", "assistants")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Chats Section */}
            <div className="flex justify-center">
              <div className="w-full sm:w-3/4 max-w-4xl min-h-0">
                <WorkspaceChats
                  workspace={workspace}
                  threadDetails={threadDetails}
                  loadingThreads={loadingThreads}
                  onCreateChat={handleCreateChat}
                  creatingChat={creatingChat}
                  onRefreshThreads={async () => {
                    // Refresh the workspace data to get updated thread list
                    try {
                      const updatedWorkspace =
                        await SupabaseWorkspaceUtils.getWorkspace(workspaceId);
                      if (updatedWorkspace) {
                        setWorkspace(updatedWorkspace);
                        setAuthWorkspaces(
                          workspaces.map((w) =>
                            w.id === workspaceId ? updatedWorkspace : w
                          )
                        );

                        // Then refresh thread details with the updated list
                        if (updatedWorkspace.workspace_threads?.length > 0) {
                          fetchThreadDetails(
                            updatedWorkspace.workspace_threads
                          );
                        } else {
                          setThreadDetails({});
                          setLoadingThreads(false);
                        }
                      }
                    } catch (error) {
                      console.error(
                        "Error refreshing workspace threads:",
                        error
                      );
                    }
                  }}
                />
              </div>
            </div>

            {/* Workflows and Tasks Section - Commented out
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
              
              <WorkspaceWorkflowsSection
                workflows={workflows}
                loadingWorkflows={loadingWorkflows}
                workspaceId={workspaceId}
                onCreateWorkflow={handleOpenWorkflowDialog}
                onCreateFromTemplate={handleOpenTemplateDialog}
              />

              
              <WorkspaceTasksSection
                tasks={tasks}
                loadingTasks={loadingTasks}
                workspaceId={workspaceId}
                onCreateTask={handleOpenTaskDialog}
              />
            </div>
            */}

            {/* Coming Soon UI */}
          </div>
        </div>
      </div>

      {/* Explicitly log each dialog's state when rendering */}
      <div style={{ display: "none" }}>
        Dialogs:{" "}
        {JSON.stringify({
          assistant: isAssistantDialogOpen,
        })}
      </div>

      {/* Dialogs */}
      <AssistantManagementDialog
        open={isAssistantDialogOpen}
        onOpenChange={(open) => handleDialogOpenChange("assistants", open)}
        workspaceId={workspaceId}
        onAssistantAction={handleAssistantAction}
      />

      {/* Add WorkspaceFiles Dialog */}
      <WorkspaceFiles
        workspaceId={workspaceId}
        vectorStoreId={vectorStoreId}
        open={isFilesDialogOpen}
        onOpenChange={(open) => handleDialogOpenChange("files", open)}
      />

      {/* Add WorkspaceSettings Dialog */}
      <WorkspaceSettingsDialog
        isOpen={isSettingsDialogOpen}
        onOpenChange={setIsSettingsDialogOpen}
        workspace={workspace}
        onSaveChanges={handleWorkspaceUpdate}
        onDeleteWorkspace={handleWorkspaceDeletion}
        isLoading={isLoading}
      />

      {/* Edit Project Dialog */}
      <EditProjectDialog
        isOpen={isEditProjectDialogOpen}
        onOpenChange={setIsEditProjectDialogOpen}
        workspace={workspace}
        onSave={handleWorkspaceUpdate}
      />

      {/* Manage Team Dialog */}
      <ManageTeamDialog
        isOpen={isManageTeamDialogOpen}
        onOpenChange={setIsManageTeamDialogOpen}
        workspace={workspace}
        initialTab={manageTeamInitialTab}
        onMembersChange={() => {
          // Refresh workspace data when team changes
          SupabaseWorkspaceUtils.getWorkspace(workspaceId).then(
            (updatedWorkspace) => {
              if (updatedWorkspace) {
                setWorkspace(updatedWorkspace);
              }
            }
          );
        }}
      />

      {/* Rename Project Dialog */}
      <ProjectRenameDialog
        isOpen={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
        workspaceToRename={workspace}
        newWorkspaceName={newWorkspaceName}
        setNewWorkspaceName={setNewWorkspaceName}
        isRenamingWorkspace={isRenamingWorkspace}
        onRename={handleWorkspaceRename}
        onCancel={() => setIsRenameDialogOpen(false)}
      />

      {/* Delete Project Dialog */}
      <ProjectDeleteDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        workspaceToDelete={workspace}
        isDeletingWorkspace={isDeletingWorkspace}
        onDelete={handleWorkspaceDeletion}
        onCancel={() => setIsDeleteDialogOpen(false)}
      />
    </>
  );
}
